import React from 'react';
import { Heart, Info } from 'lucide-react';

const GivePage = () => {

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#f17b21] to-[#d66a1a] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Heart className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-5xl font-bold mb-4">Give</h1>
          <p className="text-xl text-white/90">
            "Each one must give as he has decided in his heart, not reluctantly or under compulsion, 
            for God loves a cheerful giver." - 2 Corinthians 9:7 (ESV)
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Important Notice */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 sm:p-6 mb-6 sm:mb-8 rounded-lg">
          <div className="flex items-start gap-3">
            <Info className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2 text-sm sm:text-base">Before You Give - Please Consider</h3>
              <p className="text-blue-800 mb-2 text-sm">
                We encourage you to give only as the Lord leads you. Before giving to the church, 
                please prayerfully ensure:
              </p>
              <ul className="list-disc list-inside text-blue-800 space-y-1 ml-2 sm:ml-4 text-sm">
                <li>You have had a born-again experience with Jesus Christ</li>
                <li>You are meeting your family's needs first (1 Timothy 5:8)</li>
                <li>You are living debt-free or working toward financial freedom</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Biblical Foundation */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Heart on Giving</h2>
          
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              At El Bethel Church, we believe giving is a privilege, not an obligation. 
              Your gift should flow from a heart of worship and gratitude, never from pressure or guilt.
            </p>

            <div className="bg-orange-50 border-l-4 border-[#f17b21] p-6 my-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Key Scriptures on Giving (ESV)</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-900">2 Corinthians 9:7</p>
                  <p className="italic">
                    "Each one must give as he has decided in his heart, not reluctantly or under compulsion, 
                    for God loves a cheerful giver."
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">1 Timothy 5:8</p>
                  <p className="italic">
                    "But if anyone does not provide for his relatives, and especially for members of his household, 
                    he has denied the faith and is worse than an unbeliever."
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Matthew 6:33</p>
                  <p className="italic">
                    "But seek first the kingdom of God and his righteousness, and all these things will be added to you."
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Malachi 3:10</p>
                  <p className="italic">
                    "Bring the full tithe into the storehouse, that there may be food in my house. 
                    And thereby put me to the test, says the LORD of hosts, if I will not open the windows 
                    of heaven for you and pour down for you a blessing until there is no more need."
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How Your Gift is Used</h3>
            <p>
              Every dollar given to El Bethel Church goes directly toward:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Supporting gospel preaching and Bible teaching</li>
              <li>Maintaining our worship facility and ministry operations</li>
              <li>Outreach and missions work</li>
              <li>Supporting those in need within our congregation</li>
              <li>Youth and children's ministry</li>
            </ul>

            <div className="bg-gray-50 p-6 rounded-lg mt-6">
              <h4 className="font-bold text-gray-900 mb-3">Give From a Joyful Heart</h4>
              <p>
                Please know that we trust God to provide for His work. We never want you to give out of 
                guilt, manipulation, or pressure. Give only as the Holy Spirit leads you and only when 
                your heart is full of joy and thanksgiving.
              </p>
            </div>
          </div>
        </div>

        {/* Zeffy Donation Form Embed */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-[#f17b21] to-[#d66a1a] p-6 text-white text-center">
            <Heart className="w-12 h-12 mx-auto mb-3" />
            <h3 className="text-3xl font-bold mb-2">Give Now</h3>
            <p className="text-lg text-white/90">
              Give securely through our online platform
            </p>
          </div>
          <div className="p-2 sm:p-4">
            <iframe
              title="Donation form powered by Zeffy"
              src="https://www.zeffy.com/en-US/embed/donation-form/god-loves-a-cheerful-giver-2"
              className="w-full"
              style={{
                height: '800px',
                minHeight: '600px',
                border: 'none'
              }}
              allowPaymentRequest
              allowFullScreen
            />
          </div>
          <div className="bg-gray-50 p-4 text-center text-sm text-gray-600">
            <p>Secure donation processing powered by Zeffy</p>
          </div>
        </div>

        {/* Other Ways to Give */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Other Ways to Give</h3>
          <div className="space-y-4 text-gray-700">
            <div>
              <h4 className="font-semibold text-gray-900">In Person</h4>
              <p>You may give during our Sunday worship service or drop off your gift at the church office.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">By Mail</h4>
              <p>
                Mail checks to:<br />
                El Bethel Church<br />
                765 W Westchester Pkwy #541221<br />
                Grand Prairie, TX 75052
              </p>
            </div>
          </div>
        </div>

        {/* Tax Information */}
        <div className="mt-8 bg-gray-50 rounded-xl p-6">
          <p className="text-sm text-gray-600">
            El Bethel Church is a 501(c)(3) tax-exempt organization. Your gifts are tax-deductible 
            to the extent allowed by law. You will receive a receipt for your donation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GivePage;